import {createContext, type ReactNode, useCallback, useContext, useState} from "react"
import {Modal} from "./modal"
import {useTranslation} from "react-i18next";

export interface ModalConfig {
  id?: string
  title?: string | ReactNode
  width?: number
  children: ReactNode
  footer?: ReactNode
  className?: string
  classNames?: {
    body?: string
    content?: string
    footer?: string
  }
  type?: "default" | "confirm" | "info" | "warning" | "error"
  confirmText?: string
  cancelText?: string
  open?: boolean
  centered?: boolean
  maskClosable?: boolean
  modalRender?: (modal: ReactNode) => ReactNode
  onConfirm?: () => void | Promise<void>
  onClose?: () => void | Promise<void>
  onCancel?: () => void
  showCancel?: boolean
}

interface ModalContextType {
  open: (config: Omit<ModalConfig, "id">) => void
  confirm: (config: {
    title?: string
    children: ReactNode
    onClose?: () => void | Promise<void>
    onConfirm?: () => void | Promise<void>
    confirmText?: string
    cancelText?: string
  }) => void
  info: (config: {
    title?: string;
    children: ReactNode,
    onClose?: () => void | Promise<void>
  }) => void
  warning: (config: {
    title: string;
    children: ReactNode;
    onConfirm?: () => void | Promise<void>
  }) => void
  error: (config: {
    title: string;
    children: ReactNode
  }) => void
  close: (id: string) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export function ModalProvider({children}: {
  children: ReactNode
}) {
  const [modals, setModals] = useState<ModalConfig[]>([])
  const {t} = useTranslation("common");
  
  const open = useCallback((config: Omit<ModalConfig, "id">) => {
    const id = Math.random()
                   .toString(36)
                   .substring(2, 9)
    const modal: ModalConfig = {id, ...config}
    setModals((prev) => [...prev, modal])
  }, [])
  
  const close = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id))
  }, [])
  
  const confirm = useCallback((config: {
    title?: string
    children: ReactNode
    onConfirm?: () => void | Promise<void>
    onClose?: () => void | Promise<void>
    confirmText?: string
    cancelText?: string
  }) => {
    open({
      ...config,
      title: config.title || t("popup.title"),
      type: "confirm",
      showCancel: true,
      confirmText: config.confirmText || t("popup.btn.ok"),
      cancelText: config.cancelText || t("popup.btn.back"),
    })
  }, [open],)
  
  const info = useCallback((config: {
    title?: string;
    children: ReactNode,
    onClose?: () => void | Promise<void>
  }) => {
    open({
      ...config, type: "info", showCancel: false, confirmText: t("popup.btn.ok"),
    })
  }, [open],)
  
  const warning = useCallback((config: {
    title: string;
    children: ReactNode;
    onConfirm?: () => void | Promise<void>
  }) => {
    open({
      ...config, type: "warning", showCancel: true, confirmText: t("popup.btn.ok"), cancelText: t("popup.btn.cancel"),
    })
  }, [open],)
  
  const error = useCallback((config: {
    title: string;
    children: ReactNode
  }) => {
    open({
      ...config, type: "error", showCancel: false, confirmText: t("popup.btn.ok"),
    })
  }, [open],)
  
  return (<ModalContext.Provider value={{open, confirm, info, warning, error, close}}>
    {children}
    {modals.map((modal) => {
      function handleClose() {
        if (modal.id) close(modal.id)
        if (modal.onClose) modal.onClose();
      }
      
      return (<Modal key={modal.id} className={"sm-modal"} {...modal} custom={false} onClose={handleClose} />)
    })}
  </ModalContext.Provider>)
}

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal must be used within ModalProvider")
  }
  return context
}
